<template>
  <div class="my-news-list">
    <el-card>
      <template #header>
        <div class="card-header">
          <h2>我的新闻</h2>
          <el-button type="primary" @click="goToPublish">
            <el-icon><Plus /></el-icon>
            发布新闻
          </el-button>
        </div>
      </template>

      <!-- 统计信息 -->
      <div class="stats-section">
        <el-row :gutter="20">
          <el-col :span="6">
            <el-statistic title="全部" :value="stats.total" />
          </el-col>
          <el-col :span="6">
            <el-statistic title="草稿" :value="stats.draft" />
          </el-col>
          <el-col :span="6">
            <el-statistic title="待审核" :value="stats.pending" />
          </el-col>
          <el-col :span="6">
            <el-statistic title="已发布" :value="stats.published" />
          </el-col>
        </el-row>
      </div>

      <!-- 筛选 -->
      <el-tabs v-model="activeTab" @tab-change="handleTabChange">
        <el-tab-pane label="全部" name="all" />
        <el-tab-pane label="草稿" name="draft" />
        <el-tab-pane label="待审核" name="pending" />
        <el-tab-pane label="已通过" name="approved" />
        <el-tab-pane label="已驳回" name="rejected" />
      </el-tabs>

      <!-- 新闻列表 -->
      <el-table
        v-loading="loading"
        :data="newsList"
        style="width: 100%"
      >
        <el-table-column label="封面" width="120">
          <template #default="{ row }">
            <el-image
              :src="row.cover || '/static/images/default-news.png'"
              fit="cover"
              style="width: 100px; height: 60px; border-radius: 4px;"
            >
              <template #error>
                <div class="image-slot">
                  <el-icon><Picture /></el-icon>
                </div>
              </template>
            </el-image>
          </template>
        </el-table-column>
        
        <el-table-column prop="title" label="标题" min-width="200" />
        
        <el-table-column label="状态" width="120">
          <template #default="{ row }">
            <el-tag v-if="row.is_draft === 1" type="info">草稿</el-tag>
            <el-tag v-else-if="row.audit_status === 0" type="warning">待审核</el-tag>
            <el-tag v-else-if="row.audit_status === 1" type="success">已通过</el-tag>
            <el-tag v-else-if="row.audit_status === 2" type="danger">已驳回</el-tag>
          </template>
        </el-table-column>
        
        <el-table-column label="标签" width="150">
          <template #default="{ row }">
            <el-tag v-if="row.is_top" type="danger" size="small">置顶</el-tag>
            <el-tag v-if="row.is_hot" type="warning" size="small">热门</el-tag>
            <el-tag v-if="row.is_recommend" type="success" size="small">推荐</el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="view_count" label="浏览量" width="100" />
        
        <el-table-column prop="create_time" label="创建时间" width="180" />
        
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button
              v-if="row.is_draft === 1 || row.audit_status === 2"
              type="primary"
              size="small"
              @click="handleEdit(row)"
            >
              编辑
            </el-button>
            <el-button
              size="small"
              @click="handleView(row)"
            >
              查看
            </el-button>
            <el-popconfirm
              title="确定删除这条新闻吗？"
              @confirm="handleDelete(row)"
            >
              <template #reference>
                <el-button
                  type="danger"
                  size="small"
                >
                  删除
                </el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper" v-if="total > 0">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :total="total"
          :page-sizes="[10, 20, 30, 50]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>

      <!-- 空状态 -->
      <el-empty v-if="!loading && newsList.length === 0" description="暂无新闻" />
    </el-card>

    <!-- 驳回原因弹窗 -->
    <el-dialog
      v-model="rejectDialogVisible"
      title="驳回原因"
      width="500px"
    >
      <el-alert
        type="error"
        :closable="false"
        show-icon
      >
        <template #title>
          <div>{{ currentRejectReason }}</div>
        </template>
      </el-alert>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
// eslint-disable-next-line no-unused-vars
import newsApi from '@/api/news'
import { Plus, Picture } from '@element-plus/icons-vue'

export default {
  name: 'MyNewsList',
  
  components: {
    Plus,
    Picture
  },
  
  data() {
    return {
      activeTab: 'all',
      pagination: {
        page: 1,
        pageSize: 10
      },
      loading: false,
      rejectDialogVisible: false,
      currentRejectReason: ''
    }
  },
  
  computed: {
    ...mapState('news', ['myNewsList', 'total']),
    ...mapGetters('news', ['draftCount', 'publishedCount']),
    
    newsList() {
      return this.myNewsList
    },
    
    stats() {
      return {
        total: this.total,
        draft: this.draftCount,
        pending: this.myNewsList.filter(n => n.audit_status === 0 && n.is_draft === 0).length,
        published: this.publishedCount
      }
    }
  },
  
  mounted() {
    this.loadMyNews()
  },
  
  methods: {
    ...mapActions('news', ['fetchMyNews', 'deleteNews']),
    
    /**
     * 加载我的新闻
     */
    async loadMyNews() {
      this.loading = true
      
      try {
        await this.fetchMyNews({
          status: this.activeTab,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        })
      } catch (error) {
        console.error('加载新闻失败:', error)
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 标签页切换
     */
    handleTabChange() {
      this.pagination.page = 1
      this.loadMyNews()
    },
    
    /**
     * 页码变化
     */
    handlePageChange(page) {
      this.pagination.page = page
      this.loadMyNews()
    },
    
    /**
     * 每页数量变化
     */
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.loadMyNews()
    },
    
    /**
     * 跳转到发布页面
     */
    goToPublish() {
      this.$router.push('/news/publish')
    },
    
    /**
     * 编辑新闻
     */
    handleEdit(news) {
      this.$router.push({
        path: '/news/publish',
        query: { id: news.id }
      })
    },
    
    /**
     * 查看新闻
     */
    handleView(news) {
      if (news.audit_status === 2) {
        // 已驳回，显示驳回原因
        this.currentRejectReason = news.audit_remark || '未提供驳回原因'
        this.rejectDialogVisible = true
      } else {
        // 跳转到详情页
        this.$router.push(`/news/detail/${news.id}`)
      }
    },
    
    /**
     * 删除新闻
     */
    async handleDelete(news) {
      try {
        await this.deleteNews(news.id)
        this.$message.success('删除成功')
        this.loadMyNews()
      } catch (error) {
        console.error('删除新闻失败:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.my-news-list {
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f7fa;
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    h2 {
      margin: 0;
      font-size: 20px;
      color: #303133;
    }
  }
  
  .stats-section {
    margin-bottom: 20px;
    padding: 20px;
    background-color: #f5f7fa;
    border-radius: 4px;
  }
  
  .image-slot {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background-color: #f5f7fa;
    color: #909399;
  }
  
  .pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
}

@media (max-width: 768px) {
  .my-news-list {
    padding: 10px;
    
    :deep(.el-table) {
      font-size: 12px;
    }
  }
}
</style>
